import { React, useEffect, useState } from 'react'
import useDetectPhone from 'hooks/useDetectPhone'
import useDetectTablet from 'hooks/useDetectTablet'
import useDetectDesktop from 'hooks/useDetectDesktop'
import { Card } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { Trans, useTranslation } from 'react-i18next'
import { colors } from 'globalStyles'
import styled from 'styled-components'
import SVGIcon from 'reusableComponents/SVGIcon'
import { ReactComponent as PenToSquare } from 'assets/svg/pen-to-square-fa.svg'
import { ReactComponent as Pencil } from 'assets/svg/pencil.svg'
import { updateUserPreferences } from 'api/accounts'
import { getUserPreferencesInfo } from 'store/recipient/recipientSlice'
import PropTypes from 'prop-types'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import { ReactComponent as CircleInfo } from 'assets/svg/circle-info.svg'
import SuccessModal from 'components/SuccessModal'
import TermsAndConditions from 'components/TermsAndConditions'
import CheckMark from 'reusableComponents/CheckMark'
import { StyledSwitch } from './SignaturePreferenceToggle.style'
import {
  pencilIconStyle,
  tableHeaderStyle,
  tableColumnStyle,
  tableContentStyle,
  tableSubContentStyle,
  StyledCard,
  StyledSubCard,
  StyledSpan,
  penToSquareIconStyle,
  circleInfoIconStyle,
  CenteredDiv,
  TermsAndConditionsLink,
} from './MyPreferences.style'

const pywBackendURL = process.env.REACT_APP_PYW_BACKEND_API

const SignaturePreferenceCondition = styled.div`
  text-align: left;

  .btn-link {
    display: inline;
    width: auto;
    margin: 0;
    padding: 0;
    border: 0;
    vertical-align: baseline;
    text-align: left;
    white-space: normal;
    line-height: inherit;
    font-size: inherit;
    font-weight: inherit;
  }
`

const SignaturePreference = ({ userPreferenceDetails }) => {
  const isPhone = useDetectPhone()
  const isTablet = useDetectTablet()
  const isDesktop = useDetectDesktop()

  let padding
  if (isPhone) padding = '16px 12px'
  else if (isTablet) padding = '24px'
  else if (isDesktop) padding = '24px'
  const prn = pywBackendURL
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const { language } = i18n
  const [isEditingSignaturePreference, setIsEditingSignaturePreference] = useState(false)
  const [showGreenCheck, setShowGreenCheck] = useState(false)
  const [releaseWithoutSignaturePreference, setReleaseWithoutSignaturePreference] = useState(
    userPreferenceDetails.releaseWithoutSignature
  )
  const [disableSaveButton, setDisableSaveButton] = useState(true)
  const [saveSignaturePreference, setSaveSignaturePreference] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const releaseWithoutSignature = releaseWithoutSignaturePreference
    ? t(`my_preferences.signature_released`)
    : t(`my_preferences.none_selected`)

  const handleChange = async () => {
    setReleaseWithoutSignaturePreference(!releaseWithoutSignaturePreference)
    setDisableSaveButton(false)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    try {
      await updateUserPreferences('signatureRelease', releaseWithoutSignaturePreference)
    } catch (err) {
      console.error('Failed to update user preferences.', err)
    } finally {
      const message = t('my_preferences.successfully_updated')
      setSaveSignaturePreference(true)
      setSuccessMessage(message)
      setShowModal(true)
      setTimeout(() => {
        setShowModal(false)
        setShowGreenCheck(true)
      }, 3000)
      setIsEditingSignaturePreference(false)
      setDisableSaveButton(true)
    }
  }

  const cancelModal = () => {
    setShowModal(false)
  }

  useEffect(() => {
    if (saveSignaturePreference) dispatch(getUserPreferencesInfo(prn))
  }, [saveSignaturePreference, dispatch])

  const editSignaturePreference = () => {
    setIsEditingSignaturePreference(true)
  }

  const baseUrl = process.env.REACT_APP_FOOTER_DOMAIN
  const termsAndConditionsLink = {
    url: t('footer.links.TC_service', { domain: baseUrl }),
    name: t('footer.TC_service'),
  }

  const handleCheckMarkComplete = () => {
    setShowGreenCheck(false)
  }

  return (
    <StyledCard>
      <Card.Header
        style={{
          backgroundColor: isEditingSignaturePreference ? `${colors.sky_blue}` : 'inherit',
          borderRadius: '8px 0',
          padding: '12px',
        }}
      >
        <div style={{ display: 'table', width: '100%' }}>
          <div style={tableHeaderStyle}>
            <SVGIcon
              Icon={PenToSquare}
              size="lg"
              color={colors.secondary_light_blue}
              aria-label="Signature preference"
              style={penToSquareIconStyle}
            />
          </div>
          <div style={{ display: 'table-cell' }}>
            <div style={tableContentStyle}>{t(`my_preferences.signature_preferences`)}</div>
            <div style={tableSubContentStyle}>{releaseWithoutSignature}</div>
          </div>
          <div style={tableColumnStyle}>
            {!isEditingSignaturePreference && !showGreenCheck && (
              <SVGIcon
                Icon={Pencil}
                size="lg"
                color={colors.primary_blue}
                aria-label="Edit signature preference"
                onClick={editSignaturePreference}
                style={pencilIconStyle}
              />
            )}
            {!isEditingSignaturePreference && showGreenCheck && (
              <CheckMark
                color={colors.primary_green}
                size="lg"
                delay="10000"
                onComplete={handleCheckMarkComplete}
              />
            )}
          </div>
        </div>
      </Card.Header>
      <Form id="saveSignaturePreferenceForm" onSubmit={handleSubmit}>
        {isEditingSignaturePreference && (
          <>
            <Card.Body style={{ padding }}>
              <Card.Title style={{ textAlign: isPhone ? 'center' : 'left' }}>
                {t(`my_preferences.signature_options`)}
              </Card.Title>
              <Card.Text>{t(`my_preferences.waive_signature`)}</Card.Text>
              <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <StyledSpan>{t(`my_preferences.release_signature`)}</StyledSpan>
                <StyledSwitch
                  language={language}
                  checked={releaseWithoutSignaturePreference}
                  disableRipple
                  onChange={handleChange}
                />
              </div>
            </Card.Body>
            {releaseWithoutSignaturePreference && (
              <StyledSubCard>
                <Card.Body>
                  <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                    <div style={{ width: '25px', marginTop: '0px', flexShrink: 0 }}>
                      <SVGIcon
                        Icon={CircleInfo}
                        size="lg"
                        color={colors.primary_black}
                        aria-label="Signature preference info"
                        style={circleInfoIconStyle}
                      />
                    </div>
                    <SignaturePreferenceCondition
                      style={{
                        display: 'block',
                        flex: 1,
                        fontSize: '14px',
                        padding: '0 12px',
                        textAlign: 'left',
                      }}
                    >
                      <Trans
                        t={t}
                        i18nKey="my_preferences.signature_preference_condition"
                        components={[
                          <TermsAndConditionsLink
                            key="signature-preference-service-terms"
                            href={termsAndConditionsLink.url}
                          />,
                          <TermsAndConditions key="signature-preference-pyw-terms" />,
                        ]}
                      />
                    </SignaturePreferenceCondition>
                  </div>
                </Card.Body>
              </StyledSubCard>
            )}
            <CenteredDiv>
              <Button
                type="submit"
                style={{ width: '98%' }}
                disabled={disableSaveButton}
                form="saveSignaturePreferenceForm"
              >
                {t('buttonActions.save')}
              </Button>
            </CenteredDiv>
          </>
        )}
        <SuccessModal show={showModal} onHide={cancelModal} message={successMessage} />
      </Form>
    </StyledCard>
  )
}

SignaturePreference.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  userPreferenceDetails: PropTypes.object.isRequired,
}

export default SignaturePreference
